import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NgFor } from '@angular/common';
import { SharedModule } from '../../../shared/shared.module';
import { Router } from '@angular/router';
import { AuthServiceService } from '@edtech-ui/core/services/authService/auth-service.service';
import { UserDetailResponse } from '@edtech-ui/core/models/Classroom/user-detail';
import { ClassroomPostDialogBoxComponent } from '@edtech-ui/dashboard/teacher/classroom/classroom-post-dialog-box/classroom-post-dialog-box.component';
import { GetClassroomByTeacher } from '@edtech-ui/core/models/Classroom/get-classroomByTeacher';
import { Roles } from '@edtech-ui/constants/roles.enum';
import { TeacherServicesService } from '@edtech-ui/core/services/teacher-services.service';
import { StudentDashboardResponse, Quiz, CardData } from '../../../core/models/interfaces/student-dashboard/student-dashboard';
import { NotificationsComponent } from '@edtech-ui/dashboard/teacher/inbox/notifications/notifications.component';
import { finalize, Subscription, tap } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { MatTooltipModule } from '@angular/material/tooltip';
import { QuizTabStatus } from '@edtech-ui/constants/quiz-nav.enum';
import { PaymentDialogBoxComponent } from '@edtech-ui/student-dashboard-v2/payment-dialog-box/payment-dialog-box.component';
import { PaymentSuccessDialogComponent } from '@edtech-ui/student-dashboard-v2/payment-success-dialog/payment-success-dialog.component';
import { PaymentHistoryHomeComponent } from '@edtech-ui/student-dashboard-v2/payment-history-home/payment-history-home.component';
import { StudentNotificationsComponent } from '@edtech-ui/student-dashboard-v2/student-notifications/student-notifications.component';


@Component({
	selector: 'app-student-dashboard-content',
	standalone: true,
	imports: [NgFor, SharedModule, NotificationsComponent, MatTooltipModule, PaymentHistoryHomeComponent,StudentNotificationsComponent ],
	templateUrl: './student-dashboard-content.component.html',
	styleUrls: ['./student-dashboard-content.component.scss'],
})
export class StudentDashboardContentComponent implements OnInit, OnDestroy {
	searchQuery = '';
	userDetail: UserDetailResponse['Data'] | null = null;
	userId: string | null = null;
	classrooms: GetClassroomByTeacher[] = [];
	cardData: CardData[] = [];
	isNotificationOpen = false;
	totalNotifications: number = 0;
	@ViewChild(NotificationsComponent) notificationsChild!: NotificationsComponent;
	subscription = new Subscription();
	QuizTabStatus = QuizTabStatus;
	isopenPaymentHistory = false;
	isPaymentHistoryOpen = false;
	isProfileMenuOpen = false;

	constructor(
		private dialog: MatDialog,
		private router: Router,
		private authService: AuthServiceService,
		private teacherService: TeacherServicesService,
		private toastr: ToastrService,
	) { }

	ngOnInit(): void {
		this.userId = this.authService.getUserId();
		if (this.userId) {
			this.getUserInfo(this.userId);
			this.getStudentDashboardData();
		}
	}

	getUserInfo(id: string) {
		this.subscription.add(
			this.authService.getUserDetails(id).subscribe({
				next: (response: UserDetailResponse) => {
					if (response.Success) {
						this.userDetail = response.Data;
					}
				},
			}));
	}

	getStudentDashboardData() {
		this.subscription.add(
			this.teacherService.getStudentDashboard().subscribe({
				next: (response: StudentDashboardResponse) => {
					if (response.Success) {
						this.cardData = [
							{
								studentcount: response.Data.active_class_count.toString(),
								title: 'Active Classes',
								subtext: 'Currently enrolled',
								image: 'assets/images/student_card_1.svg',
							},
							{
								studentcount: response.Data.quizzes_pending.toString(),
								title: 'Pending Quizzes',
								subtext: 'Due this week',
								image: 'assets/images/student_card_2.svg',
							},
							{
								studentcount: response.Data.quizzes_completed.toString(),
								title: 'Completed',
								subtext: 'Total Quizzes',
								image: 'assets/images/student_card_3.svg',
							},
							{
								studentcount: response.Data.total_courses.toString(),
								title: 'Courses',
								subtext: 'Total Courses',
								image: 'assets/images/student_card_4.svg',
							},
						];
					}
				},
			}));
	}

	openJoinDialogBox() {
		const roleId = this.authService.getRoleId();
		const userRole = roleId ? parseInt(roleId) as Roles : Roles.STUDENT;
		this.dialog.open(ClassroomPostDialogBoxComponent, {
			data: { userRole },
		});
	}

	goToClasses() {
		this.router.navigate(['/student-dashboard/classrooms']);
	}

	getFullName(): string {
		if (this.userDetail && this.userDetail.first_name) {
			return `${this.userDetail.first_name} ${this.userDetail.last_name || ''}`.trim();
		}
		return 'Guest';
	}

	openNotifications() {
		this.isNotificationOpen = !this.isNotificationOpen;
	}

	updateTotalNotifications(count: number) {
		this.totalNotifications = count;
	}

	markAllNotificationsRead() {
		if (this.notificationsChild) {
			this.notificationsChild.markAllNotificationsAsRead();
		}
	}

	logout() {
		const currentUserId = localStorage.getItem('user_id');
		this.authService.logout().pipe(
			tap((response) => {
				if (response.Success) {
					this.toastr.success('Logged out successfully.');
					this.router.navigate(['/login']);
				} else {
					this.toastr.error(response.Message || 'Logout failed');
				}
			}),
			finalize(() => {
				if (currentUserId) {
					localStorage.setItem('last_active_user_id', currentUserId);
					localStorage.removeItem(`${currentUserId}_ai_quiz_data`);
				} else {
					localStorage.removeItem('ai_quiz_data');
				}
				['token', 'user_id', 'role_id', 'email', 'ai_quiz_data', 'is_tenant_onboard', 'role_mode'].forEach(key => localStorage.removeItem(key));
			})
		).subscribe({
			error: () => {
				this.toastr.error('An error occurred during logout');
			}
		});
	}

	onViewClick(title: string) {
		switch (title) {
			case 'Active Classes':
				this.router.navigate(['/student-dashboard/classrooms']);
				break;

			case 'Pending Quizzes':
				this.router.navigate(['/student-dashboard/student-quiz'],
					{ queryParams: { tab: QuizTabStatus.PENDING } }
				);
				break;

			case 'Completed':
				this.router.navigate(['/student-dashboard/student-quiz'],
					{ queryParams: { tab: QuizTabStatus.COMPLETE } }
				);
				break;

			case 'Courses':
				this.router.navigate(['/student-dashboard/student-course']);
				break;

			default:
				this.toastr.error("Navigation path not defined for this card");
		}
	}

	test() {
		this.dialog.open(PaymentDialogBoxComponent);
	}

	test2() {
		this.dialog.open(PaymentSuccessDialogComponent)
	}

	closePaymentHistory() {
		this.isPaymentHistoryOpen = false;
	}

	toggleProfileMenu() {
		this.isProfileMenuOpen = !this.isProfileMenuOpen;
	}

	closeProfileMenu() {
		this.isProfileMenuOpen = false;
	}

	openPaymentHistory() {
		this.isPaymentHistoryOpen = true;
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}
}